import React, { useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import SortableListItem from "./SortableListItem";

export default function UploadImages() {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const dropRef = useRef(null);

  const handleImageUpload = (files) => {
    const fileList = Array.from(files);
    const previews = fileList.map((file) => ({
      id: `${file.name}-${Math.random()}`,
      file,
      url: URL.createObjectURL(file),
    }));
    setUploadedImages((prev) => [...prev, ...previews]);
  };

  const handleFileInputChange = (e) => {
    handleImageUpload(e.target.files);
  };

  const handleDelete = (id) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== id));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // over가 null일 수 있음 → 방어 코드 추가
    if (!over || active.id === over.id) return;

    const oldIndex = uploadedImages.findIndex((img) => img.id === active.id);
    const newIndex = uploadedImages.findIndex((img) => img.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      setUploadedImages((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div
        ref={dropRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-xl p-8 transition flex flex-col items-center justify-center text-center ${
          isDragOver
            ? "bg-blue-100 border-blue-400"
            : "bg-gray-50 border-gray-300"
        }`}
      >
        {/* 이미지 아이콘 */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-10 w-10 text-gray-400 mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 11l3 3 5-5"
          />
        </svg>

        {/* 텍스트 */}
        <p className="text-base font-medium text-gray-900 mb-1">
          Upload Images
        </p>
        {/* 숨겨진 파일 input */}
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          id="upload-input"
        />
        <label
          htmlFor="upload-input"
          className="cursor-pointer inline-block bg-blue-50 text-blue-700 font-medium py-2 px-4 rounded-full hover:bg-blue-100 text-sm"
        >
          Drop&Drag Or Choose Images To Upload
        </label>
      </div>
      {uploadedImages.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={uploadedImages.map((img) => img.id)}
            strategy={rectSortingStrategy}
          >
            <div className="mt-6 grid grid-cols-3 gap-4">
              {uploadedImages.map((img) => (
                <SortableListItem
                  key={img.id}
                  id={img.id}
                  index={img.index}
                  url={img.url}
                  onDelete={() => handleDelete(img.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
